import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Clapperboard, Film, Globe, ImageIcon, Sparkles, Loader2, RefreshCw, Check, Megaphone, Pencil, Save, X,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { AutonomyPill } from "@/components/nadia/Badges";
import { VideoPreview } from "@/components/nadia/VideoPreview";
import { ClientMedia } from "@/components/nadia/ClientMedia";
import { usePlanFeatures, ProFeatureNotice } from "@/components/nadia/PlanFeatures";
import { Switch } from "@/components/ui/switch";
import { useCreditGate } from "@/components/nadia/CreditGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  getVideo, getVideoStatus, generateVideo, patchVideo, approveVideo, linkVideoToCampaign, getVideoCost, getVideoJob, API,
} from "@/lib/api";

const TYPES = [
  { id: "reel", label: "Crea Reel", desc: "Verticale, 10-15 secondi, ritmo veloce", icon: Clapperboard },
  { id: "ad_video", label: "Crea Video pubblicitario", desc: "Più narrativo, adatto anche al feed e a YouTube", icon: Film },
];
const SOURCES = [
  { id: "site_material", label: "Usa materiale trovato sul sito", desc: "Foto e testi già estratti dal Business Brain", icon: Globe },
  { id: "own_media", label: "Usa mie foto / miei video", desc: "Carica i tuoi file: Nadia li usa per primi", icon: ImageIcon },
  { id: "ai_generated", label: "Crea materiale con AI", desc: "Nadia descrive le scene da generare", icon: Sparkles },
];

const STATUS = {
  draft: { label: "Bozza", cls: "bg-amber-500/10 text-amber-300 border-amber-500/25" },
  approved: { label: "Approvato", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" },
  rendering: { label: "In generazione", cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25" },
  in_campaign: { label: "In campagna", cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25" },
};

export default function VideoStudio() {
  const planAccess = usePlanFeatures();
  const canGenerateAI = planAccess.allows("ai_video");
  const [status, setStatus] = useState(null);
  const [project, setProject] = useState(undefined);
  const [videoType, setVideoType] = useState("reel");
  const [source, setSource] = useState("site_material");
  const [format, setFormat] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(false);
  const [mediaCount, setMediaCount] = useState(0);
  const [job, setJob] = useState(null);
  const { gate, dialog: creditDialog } = useCreditGate();

  useEffect(() => {
    if (!project || project.status !== "rendering") return;
    let stop = false;
    const tick = async () => {
      try {
        const j = await getVideoJob();
        if (stop) return;
        setJob(j);
        if (j?.status === "completed" || j?.status === "failed") {
          const p = await getVideo();
          setProject(p);
          toast[j.status === "completed" ? "success" : "error"](j.status === "completed" ? "Il tuo video è pronto" : `Generazione non riuscita: ${j.error || ""}`);
          return;
        }
      } catch { /* retry */ }
      if (!stop) setTimeout(tick, 8000);
    };
    tick();
    return () => { stop = true; };
  }, [project?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getVideoStatus().then(setStatus).catch(() => setStatus(null));
    getVideo().then((p) => { setProject(p); if (p) { setVideoType(p.video_type); setSource(p.source); setFormat(p.format); } }).catch(() => setProject(null));
  }, []);

  const run = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      const p = await fn();
      setProject(p);
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(null);
    }
  };

  const generate = () => run("generate", () => generateVideo({ video_type: videoType, source, format: format || null, ...(userPrompt.trim() ? { user_prompt: userPrompt.trim() } : {}) }),
    project ? "Nadia ha rigenerato il progetto video" : "Progetto video pronto: guarda l'anteprima");
  const approve = async () => {
    const { action } = await getVideoCost().catch(() => ({ action: "ai_reel" }));
    return gate(action, (op_id) => run("approve", () => approveVideo(op_id), "Video inviato a Google Veo: ti avviso appena è pronto"));
  };
  const use = () => run("use", linkVideoToCampaign, "Video collegato alla campagna");

  const formats = status?.formats || [];

  return (
    <>
      {creditDialog}
      <PageHeader
        eyebrow="Video / Reel Studio"
        title="Il tuo video, pronto da Nadia"
        subtitle="Attività, prodotto, pubblico e offerta li conosco già dal Business Brain. Tu scegli cosa vuoi, io scrivo idea, storyboard, scene, voce e sottotitoli."
      />

      <ProFeatureNotice access={planAccess} feature="ai_video" />
      {planAccess.ready && !planAccess.error && !canGenerateAI && source !== "own_media" && <div className="mb-6" data-testid="start-own-media"><ClientMedia onChange={(items) => setMediaCount(items.filter((m) => m.selected).length)} /></div>}

      {/* Choices */}
      <div className="nadia-card p-5 sm:p-6 mb-6" data-testid="video-choices">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Cosa vuoi creare</div>
            <div className="space-y-2">
              {TYPES.map((t) => <Choice key={t.id} item={t} active={videoType === t.id} onClick={() => setVideoType(t.id)} testId={`video-type-${t.id}`} />)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Con quale materiale</div>
            <div className="space-y-2">
              {SOURCES.map((t) => <Choice key={t.id} item={t} active={source === t.id} onClick={() => setSource(t.id)} testId={`video-source-${t.id}`} />)}
            </div>
          </div>
        </div>
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Formato <span className="text-slate-500 normal-case tracking-normal">(lascia vuoto: lo consiglia Nadia)</span></div>
          <div className="flex gap-2 flex-wrap" data-testid="video-formats">
            <Chip active={format === ""} onClick={() => setFormat("")} label="Consigliato da Nadia" testId="video-format-auto" />
            {formats.map((f) => <Chip key={f.id} active={format === f.id} onClick={() => setFormat(f.id)} label={`${f.label} ${f.ratio}`} testId={`video-format-${f.id}`} />)}
          </div>
        </div>
        <div className="mt-5">
          <label htmlFor="video-user-prompt" className="block text-sm font-semibold text-slate-100 mb-2" data-testid="video-user-prompt-label">
            Cosa vuoi nel video <span className="text-xs font-normal text-slate-400">(opzionale)</span>
          </label>
          <Textarea
            id="video-user-prompt"
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            placeholder="Descrivi cosa vorresti vedere nel video"
            rows={3}
            className="bg-[#0B1120] border-white/10 text-slate-100"
            aria-describedby="video-user-prompt-hint"
            data-testid="video-user-prompt"
          />
          <p id="video-user-prompt-hint" className="mt-2 text-xs text-slate-400" data-testid="video-user-prompt-hint">
            Lascia vuoto per lasciare la scelta a Nadia.
          </p>
        </div>
        {source === "own_media" && (
          <div className="mt-5"><ClientMedia onChange={(items) => setMediaCount(items.filter((m) => m.selected).length)} /></div>
        )}
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <Button onClick={generate} disabled={!canGenerateAI || !!busy || (source === "own_media" && mediaCount === 0)} className="h-11 px-5 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold rounded-xl" data-testid="video-generate-button">
            {busy === "generate" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Clapperboard size={15} className="mr-2" />}
            {project ? "Rigenera progetto" : videoType === "reel" ? "Genera reel" : "Genera video"}
          </Button>
          {source === "own_media" && mediaCount === 0 && <span className="text-xs text-amber-300" data-testid="media-required-hint">Carica almeno una foto o un video</span>}
          <span className="text-xs text-slate-500">
            Motore video: <span className={status?.mode === "live" ? "text-emerald-300" : "text-amber-300"} data-testid="video-api-mode">{status?.mode === "live" ? `collegato (${status.model})` : "Google Veo non ancora configurato"}</span>
          </span>
        </div>
      </div>

      {busy === "generate" && !project && (
        <div className="nadia-card p-8 flex items-center gap-3 text-slate-400"><NadiaAvatar size="sm" thinking /> Nadia sta scrivendo il video…</div>
      )}

      {project && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-6 items-start" data-testid="video-project">
          <div className="space-y-4">
            <div className="nadia-card p-5">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <NadiaAvatar size="sm" />
                <div className="font-heading text-lg font-semibold text-slate-100">Progetto video</div>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${STATUS[project.status]?.cls || STATUS.draft.cls}`} data-testid="video-status">{STATUS[project.status]?.label || project.status}</span>
                <AutonomyPill level="PREPARA" />
                {project.regenerations > 0 && <span className="text-[11px] text-slate-500">rigenerato {project.regenerations}×</span>}
              </div>
              {editing ? (
                <Editor project={project} onCancel={() => setEditing(false)} onSave={async (patch) => { await run("save", () => patchVideo(patch), "Modifiche salvate"); setEditing(false); }} />
              ) : (
                <>
                  <Block label="Idea" testId="video-idea">{project.idea}</Block>
                  <Block label="Hook (prime 2 secondi)">{project.hook}</Block>
                  <Block label="Formato consigliato" testId="video-format-reco">
                    <span className="text-cyan-200 font-semibold">{project.format_label} {project.ratio}</span> — {project.format_reason}
                  </Block>
                  <Block label="Storyboard e scene" testId="video-scenes">
                    <ol className="space-y-2">
                      {project.scenes.map((s) => (
                        <li key={s.n} className="grid grid-cols-[28px_1fr] gap-2 text-sm">
                          <span className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 text-[11px] font-bold text-slate-300 flex items-center justify-center">{s.n}</span>
                          <div>
                            <div className="text-slate-100">{s.visual} <span className="text-slate-500 text-xs">· {s.duration}s</span></div>
                            <div className="text-xs mt-0.5"><span className="text-violet-300 font-semibold">Testo a video:</span> <span className="text-slate-200">{s.on_screen_text}</span></div>
                            <div className="text-xs"><span className="text-cyan-300 font-semibold">Voce:</span> <span className="text-slate-300">{s.voiceover}</span></div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </Block>
                  <Block label="Sottotitoli" testId="video-subtitles">
                    <div className="space-y-0.5 font-mono text-xs text-slate-400">
                      {project.subtitles.map((s, i) => <div key={i}>{fmtT(s.start)} → {fmtT(s.end)}  <span className="text-slate-200 font-sans">{s.text}</span></div>)}
                    </div>
                  </Block>
                  <div className="grid grid-cols-2 gap-3">
                    <Block label="CTA finale" testId="video-cta"><span className="font-bold text-slate-50">{project.cta}</span></Block>
                    <Block label="Musica">{project.music_mood}</Block>
                  </div>
                </>
              )}
            </div>

            {!editing && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="video-actions">
                <Button variant="outline" onClick={() => setEditing(true)} disabled={!!busy} className="border-white/10 text-slate-200 bg-transparent hover:text-cyan-200 hover:border-cyan-400/50" data-testid="video-edit-button"><Pencil size={14} className="mr-2" /> Modifica</Button>
                <Button variant="outline" onClick={generate} disabled={!canGenerateAI || !!busy} className="border-white/10 text-slate-200 bg-transparent hover:text-violet-200 hover:border-violet-400/50" data-testid="video-regenerate-button">
                  {busy === "generate" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />} Rigenera
                </Button>
                <Button onClick={approve} disabled={!canGenerateAI || !!busy || project.status !== "draft" || status?.mode !== "live"} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="video-approve-button">
                  {busy === "approve" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />} Approva video
                </Button>
                <Button onClick={use} disabled={!!busy || project.status !== "approved"} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold" data-testid="video-use-button">
                  {busy === "use" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Megaphone size={14} className="mr-2" />} Usa nella campagna
                </Button>
              </div>
            )}
            {project.render && (
              <div className="text-xs text-slate-500" data-testid="video-render-info">
                Motore: Google Veo · stato {project.status === "rendering" ? (job?.status || "in coda") : project.render.status} · job {project.render.job_id}
                {project.render.error && <span className="text-rose-300"> · {project.render.error}</span>}
              </div>
            )}
            {project.render?.video_url && (
              <video controls className="w-full rounded-xl border border-white/10 bg-black max-h-[420px]" src={`${API.replace(/\/api$/, "")}${project.render.video_url}`} data-testid="video-player" />
            )}
          </div>

          <div className="lg:sticky lg:top-6">
            <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold mb-3">Anteprima del progetto</div>
            <VideoPreview project={project} onPatch={(patch) => run("save", () => patchVideo(patch), "Testo della voce salvato")} />
          </div>
        </div>
      )}
    </>
  );
}

function Choice({ item, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${active ? "bg-cyan-500/10 border-cyan-500/30" : "border-white/10 hover:border-white/20 bg-white/[0.02]"}`}>
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-cyan-400/20 text-cyan-200" : "bg-white/[0.04] text-slate-400"}`}><item.icon size={16} /></span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-100">{item.label}</span>
        <span className="block text-xs text-slate-400">{item.desc}</span>
      </span>
    </button>
  );
}

function Chip({ active, onClick, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-cyan-500/10 text-cyan-200 border-cyan-500/30" : "text-slate-400 border-white/10 hover:text-slate-100 hover:border-white/20"}`}>{label}</button>
  );
}

function Block({ label, children, testId }) {
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{label}</div>
      <div className="text-sm text-slate-200 leading-relaxed">{children}</div>
    </div>
  );
}

function Editor({ project, onSave, onCancel }) {
  const [idea, setIdea] = useState(project.idea);
  const [hook, setHook] = useState(project.hook);
  const [cta, setCta] = useState(project.cta);
  const [scenes, setScenes] = useState(project.scenes.map((s) => ({ ...s })));
  const [music, setMusic] = useState(project.music_mood || "");
  const [voice, setVoice] = useState(project.voiceover_enabled !== false);
  const [subs, setSubs] = useState(project.subtitles_enabled !== false);
  const [poster, setPoster] = useState(project.poster_url);
  const upd = (i, k, v) => setScenes((sc) => sc.map((s, j) => (j === i ? { ...s, [k]: k === "duration" ? Number(v) || 3 : v } : s)));
  const moveScene = (i, d) => setScenes((sc) => { const j = i + d; if (j < 0 || j >= sc.length) return sc; const c = [...sc]; [c[i], c[j]] = [c[j], c[i]]; return c.map((x, k) => ({ ...x, n: k + 1 })); });
  return (
    <div className="space-y-3" data-testid="video-editor">
      <Field label="Idea"><Textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={2} className="bg-[#0B1120] border-white/10 text-slate-100" data-testid="edit-idea" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Hook"><Input value={hook} onChange={(e) => setHook(e.target.value)} className="bg-[#0B1120] border-white/10 text-slate-100 h-10" data-testid="edit-hook" /></Field>
        <Field label="CTA"><Input value={cta} onChange={(e) => setCta(e.target.value.toUpperCase())} className="bg-[#0B1120] border-white/10 text-slate-100 h-10" data-testid="edit-cta" /></Field>
      </div>
      {scenes.map((s, i) => (
        <div key={i} className="rounded-xl border border-white/10 p-3 space-y-2" data-testid={`edit-scene-${i + 1}`}>
          <div className="flex items-center gap-2 text-xs text-slate-400 font-semibold flex-wrap">
            Scena {i + 1} · <Input type="number" value={s.duration} onChange={(e) => upd(i, "duration", e.target.value)} className="w-16 h-7 bg-[#0B1120] border-white/10 text-slate-100 text-xs" /> sec
            {s.media_url && (
              <select value={s.crop || "center"} onChange={(e) => upd(i, "crop", e.target.value)} className="h-7 rounded-md bg-[#0B1120] border border-white/10 text-slate-200 text-xs px-2" data-testid={`edit-scene-crop-${i + 1}`}>
                <option value="center">Crop: centro</option><option value="top">Crop: alto</option><option value="bottom">Crop: basso</option>
              </select>
            )}
            {s.media_url && <button type="button" onClick={() => setPoster(s.media_url)} className={`h-7 px-2 rounded-md border text-xs ${poster === s.media_url ? "border-amber-400 text-amber-300" : "border-white/10 text-slate-400 hover:text-slate-200"}`} data-testid={`edit-scene-cover-${i + 1}`}>{poster === s.media_url ? "Cover ✓" : "Usa come cover"}</button>}
            <span className="ml-auto flex gap-1">
              <button type="button" onClick={() => moveScene(i, -1)} className="h-7 w-7 rounded-md border border-white/10 text-slate-300 hover:text-cyan-200" data-testid={`edit-scene-up-${i + 1}`}>↑</button>
              <button type="button" onClick={() => moveScene(i, 1)} className="h-7 w-7 rounded-md border border-white/10 text-slate-300 hover:text-cyan-200" data-testid={`edit-scene-down-${i + 1}`}>↓</button>
            </span>
          </div>
          <Input value={s.visual} onChange={(e) => upd(i, "visual", e.target.value)} placeholder="Inquadratura" className="bg-[#0B1120] border-white/10 text-slate-100 h-9 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={s.on_screen_text} onChange={(e) => upd(i, "on_screen_text", e.target.value)} placeholder="Testo a video" className="bg-[#0B1120] border-white/10 text-slate-100 h-9 text-sm" data-testid={`edit-scene-text-${i + 1}`} />
            <Input value={s.voiceover} onChange={(e) => upd(i, "voiceover", e.target.value)} placeholder="Voce narrante" className="bg-[#0B1120] border-white/10 text-slate-100 h-9 text-sm" />
          </div>
        </div>
      ))}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <Field label="Musica"><Input value={music} onChange={(e) => setMusic(e.target.value)} className="bg-[#0B1120] border-white/10 text-slate-100 h-10" data-testid="edit-music" /></Field>
        <label className="flex items-center justify-between gap-2 rounded-xl border border-white/10 px-3 h-10 text-sm text-slate-200"><span>Voce Nadia</span><Switch checked={voice} onCheckedChange={setVoice} data-testid="edit-voice-toggle" /></label>
        <label className="flex items-center justify-between gap-2 rounded-xl border border-white/10 px-3 h-10 text-sm text-slate-200"><span>Sottotitoli</span><Switch checked={subs} onCheckedChange={setSubs} data-testid="edit-subs-toggle" /></label>
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave({ idea, hook, cta, scenes, music_mood: music, voiceover_enabled: voice, subtitles_enabled: subs, poster_url: poster })} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="video-save-button"><Save size={14} className="mr-2" /> Salva</Button>
        <Button variant="ghost" onClick={onCancel} className="text-slate-400 hover:text-slate-200" data-testid="video-cancel-edit"><X size={14} className="mr-2" /> Annulla</Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}

const fmtT = (s) => `0:${String(s).padStart(2, "0")}`;
